/**
 * アプリ状態ストア。エンジン状態は日次で区切り、日次集計のみサーバへ同期する（§2）。
 */
import { areaById, DEFAULT_ROUTE_PREFS, findAreaFor, initialEngineState } from "@uvk/shared";
import type {
  AlertEvent,
  DailyAggregate,
  EngineState,
  LatLon,
  Product,
  RoutePrefs,
  TerrainArea,
  TickResult,
  UserAccount,
  UserSettings,
} from "@uvk/shared";
import {
  adoptOrphanRecords,
  db,
  listProducts,
  listUsers,
  saveDayState,
  type DayStateRow,
} from "./db.js";

import { postAggregates } from "./api.js";

export function jstDate(now: number): string {
  return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

export function jstHour(now: number): number {
  return new Date(now + 9 * 3600 * 1000).getUTCHours();
}

/** 端末内のみに保存するローカル設定（自宅座標は端末外に出さない、§2） */
export interface LocalPrefs {
  home: LatLon | null;
  autoStart: boolean;
  /** レシートの案分に使う世帯人数（端末内のみ） */
  householdSize?: number;
  /** 実年齢。肌年齢との差を出すのに使う（端末内のみ） */
  chronologicalAge?: number;
  /** 使う地形エリア（shared/areas.ts のid）。未設定なら既定エリア */
  areaId?: string;
}

export interface AppState {
  settings: UserSettings;
  localPrefs: LocalPrefs;
  engine: EngineState;
  date: string;
  products: Product[];
  sessionActive: boolean;
  sessionStartedAt: number | null;
  lastDetail: TickResult["detail"] | null;
  alertsLog: AlertEvent[];
  degraded: boolean;
  /** この端末に作られたユーザー（accounts.ts）。サーバには無い */
  users: UserAccount[];
  /** ログイン中のユーザーid。未ログインなら null */
  currentUserId: string | null;
}

export const state: AppState = {
  settings: { skinType: 2, heightMode: "adult", routePrefs: { ...DEFAULT_ROUTE_PREFS } },
  localPrefs: { home: null, autoStart: false, householdSize: 1 },
  engine: initialEngineState(),
  date: jstDate(Date.now()),
  products: [],
  sessionActive: false,
  sessionStartedAt: null,
  lastDetail: null,
  alertsLog: [],
  degraded: false,
  users: [],
  currentUserId: null,
};

/** 経路探索の時間上限設定（未設定なら既定値） */
export function routePrefs(): RoutePrefs {
  return state.settings.routePrefs ?? { ...DEFAULT_ROUTE_PREFS };
}

/** いま使っている地形エリア */
export function activeArea(): TerrainArea {
  return areaById(state.localPrefs.areaId);
}

/**
 * 現在地が別のエリアの中なら、そのエリアへ切り替える。
 * 既定は東京なので、広島で開いた人が設定を触らないと使えない状態だった。
 * 切り替えたら true を返す（呼び出し側でタイル読み込み・再描画を行う）。
 */
export async function maybeSwitchArea(pos: LatLon): Promise<boolean> {
  const found = findAreaFor(pos);
  if (!found || found.id === activeArea().id) return false;
  await saveLocalPrefs({ ...state.localPrefs, areaId: found.id });
  return true;
}

/** ログイン中のユーザー。未ログインなら null */
export function currentUser(): UserAccount | null {
  return state.users.find((u) => u.id === state.currentUserId) ?? null;
}

/**
 * ログイン状態を保存する。次に開いたときも同じ人のままにしたいので端末内に残す。
 * パスワードは保存しない（保存しているのはidだけ）。
 */
export async function setCurrentUser(userId: string | null): Promise<void> {
  state.currentUserId = userId;
  if (userId) await db.put("kv", { userId, since: Date.now() }, "session");
  else await db.delete("kv", "session");
  notify();
}

/** ユーザー一覧を読み直す（登録・削除のあと） */
export async function reloadUsers(): Promise<void> {
  state.users = await listUsers();
  if (state.currentUserId && !state.users.some((u) => u.id === state.currentUserId)) {
    await setCurrentUser(null);
  }
  notify();
}

/**
 * 最初のユーザーを作ったときだけ、アカウント以前の記録を引き取る。
 * 2人目以降で引き取ると、1人目の記録が混ざってしまうので行わない。
 */
export async function adoptRecordsIfFirstUser(userId: string): Promise<number> {
  if (state.users.length > 1) return 0;
  return adoptOrphanRecords(userId);
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function notify(): void {
  for (const fn of listeners) fn();
}

export async function loadPersisted(): Promise<void> {
  const settings = await db.get<UserSettings>("kv", "settings");
  if (settings) {
    state.settings = { ...settings, routePrefs: { ...DEFAULT_ROUTE_PREFS, ...settings.routePrefs } };
  }
  const localPrefs = await db.get<LocalPrefs>("kv", "localPrefs");
  if (localPrefs) state.localPrefs = localPrefs;
  state.users = await listUsers();
  const session = await db.get<{ userId: string }>("kv", "session");
  // 消されたユーザーのidが残っていることがあるので、実在を確かめてから採用する
  state.currentUserId = session && state.users.some((u) => u.id === session.userId)
    ? session.userId
    : null;
  state.products = await listProducts();
  // 当日のエンジン状態を復元（アプリ再起動しても積分を引き継ぐ）
  const engine = await db.get<EngineState>("kv", `engine:${state.date}`);
  if (engine) state.engine = engine;
}

export async function saveSettings(s: UserSettings): Promise<void> {
  state.settings = s;
  await db.put("kv", s, "settings");
  notify();
}

export async function saveLocalPrefs(p: LocalPrefs): Promise<void> {
  state.localPrefs = p;
  await db.put("kv", p, "localPrefs");
  notify();
}

export function toAggregate(): DailyAggregate {
  const e = state.engine;
  return {
    date: state.date,
    sedAct: e.sAct,
    sedBare: e.sBare,
    sedAdh: e.sAdh,
    minOutdoor: e.minOutdoor,
    minSun: e.minSun,
    applications: e.applicationsToday,
    alertsSent: e.alertsToday,
    alertsFollowed: e.alertsFollowed,
    pm25ExposureDay: e.pm25ExposureMin / (24 * 60), // μg/m³·分 → μg/m³·日
  };
}

/** エンジン状態の永続化＋日次集計の保存・同期（外出モード終了時・定期） */
export async function persistDay(): Promise<void> {
  await db.put("kv", state.engine, `engine:${state.date}`);
  const row: DayStateRow = { ...toAggregate(), minUnknown: state.engine.minUnknown };
  await saveDayState(row);
  await postAggregates(toAggregate()); // 失敗しても端末内に残る（次回再送）
}

/** 日付が変わったらエンジンをリセット（前日分は保存済み） */
export async function rolloverIfNeeded(now: number): Promise<void> {
  const d = jstDate(now);
  if (d !== state.date) {
    await persistDay();
    state.date = d;
    state.engine = initialEngineState();
    state.alertsLog = [];
    notify();
  }
}

/** S_adh用の仮想塗布SPF：登録商品の最大SPF、なければ30 */
export function adherenceSpfLabel(): number {
  if (state.products.length === 0) return 30;
  return Math.max(...state.products.map((p) => p.spf));
}
